import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from 'recharts';
import type { AnnualMetrics } from '@/types/stock';
import { formatPercent } from '@/lib/analysisEngine';

interface Props {
  metrics: AnnualMetrics[];
  profitMarginTarget?: number;
  height?: number;
}

const COLORS = {
  grossMargin: '#3b82f6',
  operatingMargin: '#8b5cf6',
  profitMargin: '#10b981',
  fcfMargin: '#f59e0b',
};

export function MarginChart({ metrics, profitMarginTarget = 15, height = 280 }: Props) {
  const data = metrics.map((m) => ({
    year: m.fiscalYear,
    'Gross Margin': m.grossMargin,
    'Operating Margin': m.operatingMargin,
    'Net Margin': m.profitMargin,
    'FCF Margin': m.fcfMargin,
  }));

  const CustomTooltip = ({ active, payload, label }: {
    active?: boolean;
    payload?: Array<{ name: string; value: number; color: string }>;
    label?: string;
  }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-card border border-border rounded-lg p-3 shadow-lg text-sm">
        <p className="font-semibold text-foreground mb-1">{label}</p>
        {payload.map((entry) => (
          <p key={entry.name} style={{ color: entry.color }}>
            {entry.name}: {formatPercent(entry.value)}
          </p>
        ))}
      </div>
    );
  };

  return (
    <div className="w-full">
      <h3 className="text-sm font-semibold text-muted-foreground mb-3 uppercase tracking-wide">
        Margin Trends
      </h3>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.5} />
          <XAxis
            dataKey="year"
            tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tickFormatter={(v) => formatPercent(v)}
            tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
            axisLine={false}
            tickLine={false}
            width={52}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }} />
          <ReferenceLine
            y={profitMarginTarget}
            stroke="#f59e0b"
            strokeDasharray="4 4"
            label={{ value: `Target ${profitMarginTarget}%`, position: 'insideTopRight', fontSize: 10, fill: '#f59e0b' }}
          />
          <Line
            type="monotone"
            dataKey="Gross Margin"
            stroke={COLORS.grossMargin}
            strokeWidth={2}
            dot={{ r: 3 }}
            connectNulls={false}
          />
          <Line
            type="monotone"
            dataKey="Operating Margin"
            stroke={COLORS.operatingMargin}
            strokeWidth={2}
            dot={{ r: 3 }}
            connectNulls={false}
          />
          <Line
            type="monotone"
            dataKey="Net Margin"
            stroke={COLORS.profitMargin}
            strokeWidth={2.5}
            dot={{ r: 3 }}
            connectNulls={false}
          />
          <Line
            type="monotone"
            dataKey="FCF Margin"
            stroke={COLORS.fcfMargin}
            strokeWidth={1.5}
            strokeDasharray="5 3"
            dot={{ r: 2 }}
            connectNulls={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
